import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageTriggerName, PackageType } from '@frontend/package/types';
import { TransactionUserClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionTriggerName, TransactionUserType } from '@frontend/transaction/types';

import { isMovableTransactionState } from '../../hooks/useMoveTransaction/move-transaction';
import { findActivePackagesByType, preferPackagesAtLocation } from './bulk-package-helpers';

export interface BulkRemoveTransactionInput {
    accountId: string;
    transactionId: string;
    transactionState: TransactionStateName;
    /** Required when transaction is pick-up pending. */
    removePackageWorkflowId?: string;
    actorUserId?: string;
    triggerLocationSpotId?: string;
    currentSpotId?: string | null;
    currentSlotId?: string | null;
}

export interface BulkRemoveTransactionSuccess {
    transactionId: string;
    removalPackages: Package[];
    /** Source packages/SLOTs to show in ProcessOpenCloseSlots (pick-up pending only). */
    openingPackages: Package[];
}

export interface BulkRemoveTransactionFailure {
    transactionId: string;
    error: unknown;
}

export interface BulkRemoveTransactionsResult {
    successes: BulkRemoveTransactionSuccess[];
    failures: BulkRemoveTransactionFailure[];
    openingPackages: Package[];
    removalPackages: Package[];
}

/**
 * Removes/cancels a single movable transaction.
 * Pick-up pending: skip picks → REMOVAL packages → TRANSACTION_CANCEL (REMOVAL stays pickable).
 * Drop-off pending: skip drop/pick → TRANSACTION_CANCEL (no REMOVAL, no SLOT open).
 */
export async function removeBulkTransaction(input: BulkRemoveTransactionInput): Promise<BulkRemoveTransactionSuccess> {
    const { accountId, transactionId, transactionState, removePackageWorkflowId, actorUserId, triggerLocationSpotId, currentSpotId, currentSlotId } = input;

    if (!isMovableTransactionState(transactionState)) {
        throw new Error('Transaction is not in a removable state');
    }

    const response = await PackageClient.fetchAccountTransactionPackages(accountId, transactionId);
    const packages = response.results ?? [];
    const triggerMeta = {
        location: triggerLocationSpotId,
        actor: actorUserId
    };

    if (transactionState === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        const pendingDrops = preferPackagesAtLocation(findActivePackagesByType(packages, PackageType.DROP_OFF), currentSpotId, currentSlotId);
        const pendingPicks = preferPackagesAtLocation(findActivePackagesByType(packages, PackageType.PICK_UP), currentSpotId, currentSlotId);

        for (const pkg of [...pendingDrops, ...pendingPicks]) {
            await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pkg.id, {
                trigger: PackageTriggerName.PACKAGE_SKIP,
                ...triggerMeta
            });
        }

        await TransactionWorkflowClient.triggerTransactionState(accountId, transactionId, {
            trigger: TransactionTriggerName.TRANSACTION_CANCEL
        });

        return { transactionId, removalPackages: [], openingPackages: [] };
    }

    if (!removePackageWorkflowId) {
        throw new Error('Remove package workflow is required for pick-up pending transactions');
    }

    const pendingPicks = preferPackagesAtLocation(findActivePackagesByType(packages, PackageType.PICK_UP), currentSpotId, currentSlotId);
    if (pendingPicks.length === 0) {
        throw new Error('No pending pick-up package found for removal');
    }

    if (actorUserId) {
        await TransactionUserClient.postTransactionUser(accountId, transactionId, {
            user_id: actorUserId,
            type: TransactionUserType.REMOVER
        });
    }

    const removalPackages: Package[] = [];
    for (const pendingPick of pendingPicks) {
        await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pendingPick.id, {
            trigger: PackageTriggerName.PACKAGE_SKIP,
            ...triggerMeta
        });

        const removal = await PackageClient.postPackage(accountId, transactionId, {
            iot_id: pendingPick.iot_id,
            product_id: pendingPick.product_id,
            equipment_id: pendingPick.equipment_id,
            colli_id: pendingPick.colli_id,
            spot_id: pendingPick.spot_id ?? undefined,
            module_id: pendingPick.module_id ?? undefined,
            slot_id: pendingPick.slot_id ?? undefined,
            quantity: pendingPick.quantity,
            application: Application.PUDO,
            feature: Feature.BULK_MOVE_TRANSACTION,
            priority: pendingPick.priority,
            budget_id: pendingPick.budget_id,
            cost_center_id: pendingPick.cost_center_id,
            blocking: pendingPick.blocking,
            dependent_package_id: pendingPick.id,
            workflow_id: removePackageWorkflowId,
            user_id: actorUserId ?? pendingPick.user_id,
            type: PackageType.REMOVAL
        });
        removalPackages.push({
            ...removal,
            transaction_id: removal.transaction_id ?? transactionId,
            slot_id: removal.slot_id ?? pendingPick.slot_id
        });
    }

    await TransactionWorkflowClient.triggerTransactionState(accountId, transactionId, {
        trigger: TransactionTriggerName.TRANSACTION_CANCEL
    });

    const openingPackages = removalPackages.filter((pkg) => !!pkg.slot_id);
    return { transactionId, removalPackages, openingPackages };
}

/** Best-effort bulk remove: continues after per-transaction failures. */
export async function removeBulkTransactions(inputs: BulkRemoveTransactionInput[]): Promise<BulkRemoveTransactionsResult> {
    const successes: BulkRemoveTransactionSuccess[] = [];
    const failures: BulkRemoveTransactionFailure[] = [];
    const openingPackages: Package[] = [];
    const removalPackages: Package[] = [];

    for (const input of inputs) {
        try {
            const result = await removeBulkTransaction(input);
            successes.push(result);
            openingPackages.push(...result.openingPackages);
            removalPackages.push(...result.removalPackages);
        } catch (error) {
            failures.push({ transactionId: input.transactionId, error });
        }
    }

    return { successes, failures, openingPackages, removalPackages };
}
